import { useState, useRef, useEffect } from "react";
import { Mail, MousePointerClick, Lock, Clock } from "lucide-react";
import {
  makeRequest,
  ResponseNotOkError,
  Session,
  createSession,
  executeEVMSession,
} from "@paywithglide/glide-js";
import ModalBackButton from "../components/ModalBackButton";
import TransactionCheckoutHeader from "../components/TransactionCheckoutHeader";
import PrimaryButton from "../components/PrimaryButton";
import Warning from "../components/Warning";
import { useGlideDepositConfig } from "../lib/useDepositConfig";
import { logger } from "../lib/logger";
import { AmountDisplay } from "../components/AmountDisplay";
import { FeeBreakdown } from "../components/FeeBreakdown";
import Alert from "../components/Alert";
import { TokenConversionMessage } from "../components/TokenConversionMessage";
import { AnimatePresence } from "framer-motion";
import { getConnectedWallet } from "../lib/getConnectedWallet";
import { createGlideTx } from "../lib/createGlideTx";
import { useGlideWallet } from "../components/wallet/GlideWalletProvider";
import { useConnectedWallet } from "../lib/useConnectedWallet";
import ConnectWallet from "./ConnectWallet";
import { PaytrieLogo } from "../logos/PaytrieLogo";

type Props = {
  withdrawAmount: string;
  onBack: () => void;
  onSuccess: (session: Session) => void;
  onDeferredTransaction: () => void;
};

type Step =
  | "enter_email"
  | "enter_otp"
  | "verifying"
  | "connect_wallet"
  | "creating_transaction"
  | "review"
  | "executing";

type OfframpTransaction = {
  quoteId: number;
  transactionId: string;
  sellCurrency: string;
  sellAmount: string;
  buyCurrency: string;
  buyAmount: string;
  paymentWalletAddress: string;
  etaInSeconds: number;
};

export default function WithdrawWithInterac({
  withdrawAmount,
  onBack,
  onSuccess,
  onDeferredTransaction,
}: Props) {
  const { glideConfig, ...depositConfig } = useGlideDepositConfig();
  const connectedWallet = useConnectedWallet();
  const { awaitDepositOwnerAddress } = useGlideWallet();
  const [step, setStep] = useState<Step>("enter_email");
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState(["", "", "", ""]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isExecuting, setIsExecuting] = useState(false);
  const [didCompletePayment, setDidCompletePayment] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const [transaction, setTransaction] = useState<OfframpTransaction | null>(
    null,
  );
  const [glideSession, setGlideSession] = useState<Session | null>(null);
  const [userToken, setUserToken] = useState<string | null>(null);
  const [resendTimer, setResendTimer] = useState(0);

  const isValidEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const sendOTP = async () => {
    if (!isValidEmail(email)) {
      setError("Please enter a valid email address");
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      await makeRequest(glideConfig, {
        url: "/paytrie/login",
        method: "POST",
        body: { email },
      });

      setStep("enter_otp");
      setResendTimer(60);
    } catch (error) {
      logger.error(`Failed to send OTP: ${error}`);

      if (error instanceof ResponseNotOkError) {
        const rawBody = error.response;
        try {
          const errorData = JSON.parse(rawBody);
          if (errorData.error) {
            setError(errorData.error);
          } else {
            setError("Unable to send verification code. Please try again.");
          }
        } catch {
          setError("Unable to send verification code. Please try again.");
        }
      } else {
        setError("Unable to send verification code. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const verifyOTP = async () => {
    const otpString = otp.join("");
    if (!otpString || otpString.length < 4) {
      setError("Please enter the complete 4-digit verification code");
      return;
    }

    setIsLoading(true);
    setError(null);
    setStep("verifying");

    try {
      const { token } = await makeRequest<{ token: string }>(glideConfig, {
        url: "/paytrie/login-verify",
        method: "POST",
        body: { email, loginCode: otpString },
      });

      if (!token) {
        throw new Error("Authentication failed. Please try again.");
      }

      setUserToken(token);

      // Check if wallet is connected before creating transaction
      if (!connectedWallet.isConnected) {
        setStep("connect_wallet");
        setIsLoading(false);
        return;
      } else {
        await createWithdrawalTransaction(token);
      }
    } catch (error) {
      logger.error(`Failed to verify OTP: ${error}`);

      if (error instanceof ResponseNotOkError) {
        const rawBody = error.response;
        try {
          const errorData = JSON.parse(rawBody);
          if (errorData.error) {
            setError(errorData.error);
          } else {
            setError("Unable to verify code. Please try again.");
          }
        } catch {
          setError("Unable to verify code. Please try again.");
        }
      } else {
        setError(
          error instanceof Error
            ? error.message
            : "Unable to verify code. Please try again.",
        );
      }
      setStep("enter_otp");
    } finally {
      setIsLoading(false);
    }
  };

  const createWithdrawalTransaction = async (userToken: string) => {
    setStep("creating_transaction");

    try {
      const { address } = await getConnectedWallet();

      if (!address || !depositConfig.currency || !depositConfig.chain) {
        throw new Error("Wallet or configuration not available");
      }

      const sellCurrency = depositConfig.currency.on(depositConfig.chain);

      const txData = await makeRequest<OfframpTransaction>(glideConfig, {
        url: "/interac/offramp-transaction",
        method: "POST",
        body: {
          sellCurrency,
          sellAmount: withdrawAmount,
          userJWT: userToken,
          payerAccount: address,
        },
      });

      setTransaction(txData);

      const tx = await createGlideTx(
        { glideConfig, ...depositConfig },
        {
          amount: withdrawAmount,
          recipient: txData.paymentWalletAddress,
        },
      );

      const depositOwnerAddress = await awaitDepositOwnerAddress(
        sellCurrency.split("/")[0],
      );

      // @ts-expect-error: createTx return type is not properly typed
      const session = await createSession(glideConfig, {
        account: address,
        paymentCurrency: sellCurrency,
        paymentMethod: "wallet",
        preferGaslessPayment: depositConfig.preferGaslessPayment,
        depositOwnerAddress,
        ...tx,
      });

      setGlideSession(session);
      setStep("review");
    } catch (error) {
      logger.error(`Failed to create transaction: ${error}`);

      if (error instanceof ResponseNotOkError) {
        const rawBody = error.response;
        try {
          const errorData = JSON.parse(rawBody);
          if (errorData.error) {
            setError(errorData.error);
          } else {
            setError("Unable to create withdrawal. Please try again.");
          }
        } catch {
          setError("Unable to create withdrawal. Please try again.");
        }
      } else {
        setError(
          error instanceof Error
            ? error.message
            : "Unable to create withdrawal. Please try again.",
        );
      }
      setStep("enter_otp");
      throw error;
    }
  };

  const handleWalletConnected = async () => {
    if (!userToken) {
      setError("Authentication token not found. Please try again.");
      setStep("enter_email");
      return;
    }

    try {
      await createWithdrawalTransaction(userToken);
    } catch (error) {
      logger.error(
        `Failed to create transaction after wallet connection: ${error}`,
      );
    }
  };

  const executeWithdrawal = async () => {
    if (!glideSession || !transaction) {
      setError("Session not ready. Please try again.");
      return;
    }

    try {
      setIsExecuting(true);
      setDidCompletePayment(false);
      setError(null);
      setStep("executing");

      const {
        chainId,
        switchChainAsync,
        sendTransactionAsync,
        signTypedDataAsync,
      } = await getConnectedWallet();

      if (!chainId) {
        throw new Error("Wallet not connected to any chain");
      }

      const result = await executeEVMSession(glideConfig, {
        session: glideSession,
        currentChainId: chainId,
        switchChainAsync,
        sendTransactionAsync,
        signTypedDataAsync,
        onCompletePayment: () => setDidCompletePayment(true),
        onSessionUpdate: (updatedSession) => {
          setGlideSession(updatedSession);

          // Call success when payment is confirmed
          if (updatedSession.paymentStatus === "paid") {
            onSuccess({
              ...updatedSession,
              etaInSeconds: transaction.etaInSeconds,
            });
          }
        },
      });

      if (result && "deferred" in result) {
        onDeferredTransaction();
      }
    } catch (error) {
      logger.error(`Failed to execute withdrawal: ${error}`);
      setError(
        `Transaction failed: ${error instanceof Error ? error.message : "Unknown error"}`,
      );
      setStep("review");
      setIsExecuting(false);
      setDidCompletePayment(false);
    }
  };

  const handleBack = () => {
    if (step === "executing") {
      setStep("review");
      setError(null);
      setIsExecuting(false);
      setDidCompletePayment(false);
    } else if (
      step === "enter_otp" ||
      step === "verifying" ||
      step === "creating_transaction" ||
      step === "review" ||
      step === "connect_wallet"
    ) {
      setStep("enter_email");
      setOtp(["", "", "", ""]);
      setError(null);
    } else {
      onBack();
    }
  };

  const handleOtpChange = (index: number, value: string) => {
    const numericValue = value.replace(/[^0-9]/g, "");

    if (numericValue.length > 1) {
      // Handle paste - distribute digits across inputs
      const digits = numericValue.slice(0, 4).split("");
      const newOtp = [...otp];
      digits.forEach((digit, i) => {
        if (index + i < 4) {
          newOtp[index + i] = digit;
        }
      });
      setOtp(newOtp);
      setError(null);

      const nextEmptyIndex = newOtp.findIndex(
        (digit, i) => i > index && !digit,
      );
      const focusIndex =
        nextEmptyIndex !== -1
          ? nextEmptyIndex
          : Math.min(index + digits.length, 3);
      inputRefs.current[focusIndex]?.focus();
      return;
    }

    const newOtp = [...otp];
    newOtp[index] = numericValue;
    setOtp(newOtp);
    setError(null);

    if (numericValue && index < 3) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpPaste = (
    index: number,
    e: React.ClipboardEvent<HTMLInputElement>,
  ) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text");
    const numericValue = pastedData.replace(/[^0-9]/g, "");

    if (numericValue) {
      const digits = numericValue.slice(0, 4 - index).split("");
      const newOtp = [...otp];
      digits.forEach((digit, i) => {
        if (index + i < 4) {
          newOtp[index + i] = digit;
        }
      });
      setOtp(newOtp);
      setError(null);

      const lastFilledIndex = Math.min(index + digits.length - 1, 3);
      const nextEmptyIndex = newOtp.findIndex(
        (digit, i) => i > lastFilledIndex && !digit,
      );
      const focusIndex =
        nextEmptyIndex !== -1 ? nextEmptyIndex : lastFilledIndex;
      inputRefs.current[focusIndex]?.focus();
    }
  };

  const handleOtpKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (e.key === "Backspace") {
      if (!otp[index] && index > 0) {
        const newOtp = [...otp];
        newOtp[index - 1] = "";
        setOtp(newOtp);
        inputRefs.current[index - 1]?.focus();
      } else if (otp[index]) {
        const newOtp = [...otp];
        newOtp[index] = "";
        setOtp(newOtp);
      }
      e.preventDefault();
    } else if (e.key === "ArrowLeft" && index > 0) {
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === "ArrowRight" && index < 3) {
      inputRefs.current[index + 1]?.focus();
    } else if (e.key === "Enter" && otp.join("").length === 4 && !isLoading) {
      verifyOTP();
    }
  };

  // Auto-focus first input when entering OTP step
  useEffect(() => {
    if (step === "enter_otp" && inputRefs.current[0]) {
      setTimeout(() => inputRefs.current[0]?.focus(), 100);
    }
  }, [step]);

  // Handle resend timer countdown
  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => {
        setResendTimer(resendTimer - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [resendTimer]);

  return (
    <>
      <ModalBackButton onClick={handleBack} />

      {step === "connect_wallet" ? (
        <ConnectWallet onConnect={handleWalletConnected} />
      ) : step === "review" || step === "executing" ? (
        transaction ? (
          <>
            <TransactionCheckoutHeader title="Review withdrawal" />

            <AmountDisplay
              label="You'll send"
              amount={transaction.sellAmount}
              currencySymbol={depositConfig.currency?.symbol || ""}
              amountUSD={transaction.sellAmount}
              currencyLogoUrl={depositConfig.currency?.logoUrl || ""}
              chainLogoUrl={depositConfig.chain?.logoUrl || ""}
            />

            <AmountDisplay
              label="You'll receive"
              amount={transaction.buyAmount}
              currencySymbol="CAD"
              currencyLogoUrl="https://upload.wikimedia.org/wikipedia/commons/thumb/f/fd/Maple_Leaf.svg/1024px-Maple_Leaf.svg.png"
              className="mt-6"
            />
            {depositConfig.currency && depositConfig.chain && (
              <TokenConversionMessage
                from={{
                  tokenSymbol: depositConfig.currency.symbol,
                  chainId: depositConfig.chain.id,
                  chainName: depositConfig.chain.name,
                }}
                to={{
                  tokenSymbol: "CAD",
                  chainId: "fiat",
                }}
                className="mt-2"
              />
            )}

            <hr className="my-4 border-0.5 border-[--color-background-secondary]" />

            <FeeBreakdown
              recipient={email}
              isEmailRecipient={true}
              showETA={true}
              etaInSeconds={transaction.etaInSeconds}
            />

            <PrimaryButton
              className="mt-4"
              loading={isExecuting}
              onClick={executeWithdrawal}
              disabled={isExecuting}
            >
              Withdraw now
            </PrimaryButton>

            {step === "executing" && !didCompletePayment ? (
              <div className="mt-4">
                <AnimatePresence mode="popLayout">
                  <Alert
                    key="continue_in_wallet"
                    icon={
                      <MousePointerClick
                        strokeWidth={2.25}
                        className="w-4 h-4"
                      />
                    }
                    title="Continue in wallet"
                    subtitle="Follow the prompts in your wallet to complete the transfer."
                  />
                </AnimatePresence>
              </div>
            ) : step === "executing" &&
              didCompletePayment &&
              glideSession?.paymentStatus === "unpaid" ? (
              <div className="mt-4">
                <AnimatePresence mode="popLayout">
                  <Alert
                    key="waiting_for_transfer"
                    icon={<Clock strokeWidth={2.25} className="w-4 h-4" />}
                    title="Waiting for transfer"
                    subtitle="We're waiting for your transfer. This takes about 15 seconds."
                  />
                </AnimatePresence>
              </div>
            ) : null}

            {error ? <Warning className="mt-4">{error}</Warning> : null}
          </>
        ) : null
      ) : (
        <>
          <TransactionCheckoutHeader
            centeredSubtitle
            title={"Verify your email"}
            subtitle={
              step === "enter_email" ? (
                "Enter your email to get started"
              ) : step === "verifying" ? (
                "Verifying your code..."
              ) : step === "creating_transaction" ? (
                "Setting up your withdrawal..."
              ) : (
                <span className="text-center">
                  Check your email for a 4-digit code
                </span>
              )
            }
          />

          <div className="space-y-4">
            {step === "enter_email" ? (
              <>
                <div>
                  <div className="mb-2 font-medium text-sm text-[--color-text-primary]">
                    Paytrie account email
                  </div>
                  <div className="relative">
                    <Mail className="w-5 h-5 text-[--color-text-secondary] absolute top-[9px] left-3" />
                    <input
                      type="email"
                      className="pl-10 w-full bg-[--color-background-card] text-[--color-text-primary] py-2 px-4 rounded-lg border border-gray-600/50 ring-0 outline-none text-sm focus:border-1 focus:border-gray-500 transition-all"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        setError(null);
                      }}
                      placeholder="your@email.com"
                      onKeyDown={(e) => {
                        if (
                          e.key === "Enter" &&
                          isValidEmail(email) &&
                          !isLoading
                        ) {
                          sendOTP();
                        }
                      }}
                      autoFocus
                      disabled={isLoading}
                      autoComplete="off"
                    />
                  </div>
                  <p className="mt-2 text-xs text-[--color-text-secondary]">
                    Don't have a Paytrie account?{" "}
                    <a
                      href="https://paytrie.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-400 hover:text-blue-300 hover:underline font-medium"
                    >
                      Sign up here
                    </a>{" "}
                    and complete KYC first.
                  </p>
                </div>

                <PrimaryButton
                  onClick={sendOTP}
                  loading={isLoading}
                  disabled={!isValidEmail(email) || isLoading}
                  className="mt-6"
                >
                  {isValidEmail(email)
                    ? "Send verification code"
                    : "Enter your email"}
                </PrimaryButton>

                {error ? <Warning className="mt-4">{error}</Warning> : null}

                <div className="mt-2 text-xs text-[--color-text-secondary] text-center">
                  We'll send a one-time code to verify your email address
                </div>

                {/* Paytrie Footer Branding */}
                <div className="flex items-center justify-center gap-1.5 mt-6 pt-4 border-t border-gray-700/30">
                  <Lock className="w-4 h-4 text-[--color-text-secondary]" />
                  <span className="text-xs text-[--color-text-secondary]">
                    Powered by
                  </span>
                  <a
                    href="https://paytrie.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center opacity-70 hover:opacity-100 transition-opacity"
                  >
                    <PaytrieLogo className="h-5 w-auto" />
                  </a>
                </div>
              </>
            ) : (
              <>
                <div className="mt-6">
                  <div className="flex gap-2 justify-center">
                    {otp.map((digit, index) => (
                      <input
                        key={index}
                        ref={(el) => (inputRefs.current[index] = el)}
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        maxLength={1}
                        value={digit}
                        onChange={(e) => handleOtpChange(index, e.target.value)}
                        onKeyDown={(e) => handleOtpKeyDown(index, e)}
                        onPaste={(e) => handleOtpPaste(index, e)}
                        onFocus={(e) => e.target.select()}
                        className="w-12 h-14 text-center text-2xl font-semibold bg-[--color-background-card] text-[--color-text-primary] rounded-lg border-2 border-gray-600/50 ring-0 outline-none focus:border-[--color-primary] transition-all"
                        disabled={isLoading}
                      />
                    ))}
                  </div>
                </div>

                <PrimaryButton
                  onClick={verifyOTP}
                  loading={isLoading}
                  disabled={otp.join("").length < 4}
                  className="mt-4"
                >
                  {otp.join("").length === 4
                    ? "Verify and continue"
                    : "Enter verification code"}
                </PrimaryButton>

                {error ? <Warning className="mt-4">{error}</Warning> : null}

                <div className="mt-3 text-sm text-[--color-text-secondary] text-center">
                  {resendTimer > 0 ? (
                    <>
                      Didn't receive it? Resend code in{" "}
                      <span className="font-medium text-[--color-text-primary]">
                        {resendTimer}s
                      </span>
                    </>
                  ) : (
                    <>
                      Didn't receive it?{" "}
                      <button
                        onClick={async () => {
                          await sendOTP();
                        }}
                        className="text-blue-400 hover:text-blue-300 hover:underline font-medium"
                      >
                        Resend code
                      </button>
                    </>
                  )}
                </div>

                {/* Paytrie Footer Branding */}
                <div className="flex items-center justify-center gap-1.5 mt-8 pt-4 border-t border-gray-700/30">
                  <Lock className="w-4 h-4 text-[--color-text-secondary]" />
                  <span className="text-xs text-[--color-text-secondary]">
                    Powered by
                  </span>
                  <a
                    href="https://paytrie.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center opacity-70 hover:opacity-100 transition-opacity"
                  >
                    <PaytrieLogo className="h-5 w-auto" />
                  </a>
                </div>
              </>
            )}
          </div>
        </>
      )}
    </>
  );
}
